import Button from '@material-ui/core/Button';
import Fade from '@material-ui/core/Fade';
import Tooltip from '@material-ui/core/Tooltip';
import { HotjarEvents, submitEvent } from 'hotjar';
import React, { useEffect, useState } from 'react';
import BlacklistMessage from 'v2/components/Shared/BlacklistMessage';
import PlgAnticipation from 'v2/components/Shared/PlgAnticipation';
import TopPage from 'v2/components/Shared/TopPage';
import Tour from 'v2/components/Shared/Tour';
import { sendEventToGA } from 'v2/helpers/analytics';
import { EFinancialStatementTabs } from 'v2/interfaces/financial';

import type { ListOrder } from 'v2/services/financialStatement/statement';

import Blocked from './Blocked';
import CardList from './CardList';
import { tourSteps } from './constants';
import ExportModal from './ExportModal';
import Filter from './Filter';
import Future from './Future';
import Statement from './Statement';
import { useStyles, CustomTab, CustomTabs } from './styles';

function FinancialStatement() {
  const classes = useStyles();

  const [shouldDisabledSortBtn, setShouldDisabledSortBtn] = useState(false);
  const [currentOrder, setCurrentOrder] = useState<ListOrder>('DESC');
  const [tab, setTab] = useState<EFinancialStatementTabs>(EFinancialStatementTabs.STATEMENT);
  const [exportModalItsOpen, setExportModalItsOpen] = useState(false);

  const showOrderButton = tab !== EFinancialStatementTabs.STATEMENT;
  const currOrderIsAscending = currentOrder === 'ASC';

  const handleChange = (_: React.ChangeEvent<{}>, newTab: EFinancialStatementTabs) => {
    const translateTabName = (tab: EFinancialStatementTabs) => {
      if (tab === EFinancialStatementTabs.STATEMENT) return 'Extrato';
      if (tab === EFinancialStatementTabs.FUTURE) return 'Lançamentos Futuros';
      return 'Lançamentos Bloqueados';
    };

    sendEventToGA({
      category: 'Novo Extrato',
      action: 'SubAba',
      label: translateTabName(newTab),
    });

    setTab(newTab);
  };

  const handleClickExport = () => {
    sendEventToGA({
      category: 'Novo Extrato',
      action: 'Botão',
      label: 'Exportar',
    });

    setExportModalItsOpen(true);
  };

  const handleToggleExportModal = () => setExportModalItsOpen((prevState) => !prevState);
  const handleChangeOrder = () => setCurrentOrder(currOrderIsAscending ? 'DESC' : 'ASC');

  const Tag = () => (
    <div className={classes.tag}>
      Wallet <strong>BRL</strong>
    </div>
  );

  const AdditionalButtons = () => (
    <>
      {showOrderButton && (
        <Tooltip title={`Ordem ${currOrderIsAscending ? '' : 'de'}crescente`} placement='top'>
          <Button className={classes.btnIcon} variant='outlined' onClick={handleChangeOrder} disabled={shouldDisabledSortBtn}>
            <img className={classes.imgIcon} src={`/img/sort_${currOrderIsAscending ? 'a' : 'de'}scending.svg`} alt={'badgeimg'} />
          </Button>
        </Tooltip>
      )}

      <Tooltip title='Exporte e analise seu extrato em períodos de até 12 meses' placement='top'>
        <Button variant='outlined' onClick={handleClickExport}>
          Exportar
        </Button>
      </Tooltip>
    </>
  );

  useEffect(() => submitEvent(HotjarEvents.NEW_STATEMENT_ACCESS), []);

  return (
    <Fade in timeout={500}>
      <div className={classes.root}>
        <BlacklistMessage />

        <TopPage
          title='Extrato Financeiro'
          extra={<Tag />}
          helpLink='https://ajuda.eduzz.com/hc/pt-br/articles/4402964669467-Extrato-Financeiro-%C3%93rbita'
        />

        <CardList />
        <Filter currentTab={tab} />
        <div id='main-results'>
          <CustomTabs onChange={handleChange} value={tab}>
            <CustomTab value={EFinancialStatementTabs.STATEMENT} label='Extrato' />
            <CustomTab value={EFinancialStatementTabs.FUTURE} label='Lançamentos Futuros' />
            <CustomTab value={EFinancialStatementTabs.BLOCKED} label='Lançamentos Bloqueados' />
            <div className={classes.buttonsContainer}>
              <AdditionalButtons />
            </div>
          </CustomTabs>

          <div className={classes.buttonsMobile}>
            <AdditionalButtons />
          </div>

          {tab === EFinancialStatementTabs.STATEMENT && <Statement />}
          {tab === EFinancialStatementTabs.FUTURE && <Future order={currentOrder} shouldDisableSortBtn={setShouldDisabledSortBtn} />}
          {tab === EFinancialStatementTabs.BLOCKED && <Blocked order={currentOrder} shouldDisableSortBtn={setShouldDisabledSortBtn} />}

          {exportModalItsOpen && <ExportModal isOpen={exportModalItsOpen} setClose={handleToggleExportModal} />}

          <Tour steps={tourSteps} name='financial_statement_tour' />
        </div>

        <PlgAnticipation page='financial-statement' />
      </div>
    </Fade>
  );
}

export default FinancialStatement;
